/**
 * CR3 questionnaire data + mutation layer. Mirrors useDocuments.ts: thin async
 * helpers over the `api` client (all ApiResult<T>, credentials included) plus a
 * couple of React hooks for the surfaces that need live state.
 *
 * Covers three audiences:
 *   - internal (coach/admin): list, detail, edit/add/delete questions,
 *     regenerate, approve, generate, distribution, resend.
 *   - owner: GET/PATCH/POST the owner_verification round.
 *   - PIC (public, magic-link token): GET/PATCH/POST under /q/:token.
 *
 * The autosave hook (useAutosave) debounces a PATCH so typing/clicking doesn't
 * spam the network, and exposes a "saving | saved | error" status for the
 * "Tersimpan otomatis" indicator.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ApiResult,
  AnswerInput,
  QuestionDTO,
  QuestionnaireDetail,
  QuestionnaireSummary,
  QuestionType,
  DistributionRow,
  DeliveryChannel,
  SavedAnswer,
} from '@klinik/shared';
import { api } from './api';

// ---- public (PIC magic-link) payload shape returned by GET /q/:token/questionnaire ----
export interface PicQuestionnaireData {
  company: { id: string; name: string };
  picName: string | null;
  division: string | null;
  questionnaire: { id: string; title: string; status: string };
  questions: QuestionDTO[];
  savedAnswers: SavedAnswer[];
  /** UU PDP: privacy-policy version the PIC must explicitly accept at submit. */
  policyVersion: string;
}

/** Owner round GET shape (GET /companies/:id/questionnaires/owner). */
export interface OwnerQuestionnaireData {
  questionnaire: QuestionnaireSummary;
  questions: QuestionDTO[];
  savedAnswers: SavedAnswer[];
}

// =============================== Internal (coach/admin) ===============================

export async function listQuestionnaires(
  companyId: string,
  filter?: { round?: string; status?: string },
): Promise<ApiResult<{ questionnaires: QuestionnaireSummary[] }>> {
  const qs = new URLSearchParams();
  if (filter?.round) qs.set('round', filter.round);
  if (filter?.status) qs.set('status', filter.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/companies/${companyId}/questionnaires${suffix}`);
}

export async function getQuestionnaire(
  companyId: string,
  qid: string,
): Promise<ApiResult<QuestionnaireDetail>> {
  return api.get(`/companies/${companyId}/questionnaires/${qid}`);
}

export async function generateQuestionnaires(
  companyId: string,
): Promise<ApiResult<{ enqueued: number; picIds: string[] }>> {
  return api.post(`/companies/${companyId}/questionnaires/generate`, { round: 'pic' });
}

export interface QuestionPatch {
  text?: string;
  type?: QuestionType;
  options?: string[] | null;
  required?: boolean;
  orderIndex?: number;
}

export async function editQuestion(
  companyId: string,
  qid: string,
  questionId: string,
  patch: QuestionPatch,
): Promise<ApiResult<{ question: QuestionDTO }>> {
  return api.patch(`/companies/${companyId}/questionnaires/${qid}/questions/${questionId}`, patch);
}

export async function addQuestion(
  companyId: string,
  qid: string,
  input: { text: string; type: QuestionType; options?: string[] | null; required?: boolean },
): Promise<ApiResult<{ question: QuestionDTO }>> {
  return api.post(`/companies/${companyId}/questionnaires/${qid}/questions`, input);
}

export async function deleteQuestion(
  companyId: string,
  qid: string,
  questionId: string,
): Promise<ApiResult<{ deleted: true }>> {
  return api.del(`/companies/${companyId}/questionnaires/${qid}/questions/${questionId}`);
}

export async function regenerateQuestionnaire(
  companyId: string,
  qid: string,
): Promise<ApiResult<{ enqueued: true }>> {
  return api.post(`/companies/${companyId}/questionnaires/${qid}/regenerate`);
}

export async function approveQuestionnaire(
  companyId: string,
  qid: string,
): Promise<ApiResult<{ sent: boolean; channels: DeliveryChannel[] }>> {
  return api.post(`/companies/${companyId}/questionnaires/${qid}/approve`);
}

export async function getDistribution(
  companyId: string,
  round: string = 'pic',
): Promise<ApiResult<{ rows: DistributionRow[]; totals: { total: number; invited: number; completed: number } }>> {
  return api.get(`/companies/${companyId}/questionnaires/distribution?round=${round}`);
}

export async function resendPic(
  companyId: string,
  picId: string,
): Promise<ApiResult<{ resent: true }>> {
  return api.post(`/companies/${companyId}/pics/${picId}/resend`);
}

// =============================== Owner verification round ===============================

export async function getOwnerQuestionnaire(
  companyId: string,
): Promise<ApiResult<OwnerQuestionnaireData>> {
  return api.get(`/companies/${companyId}/questionnaires/owner`);
}

export async function saveOwnerAnswers(
  companyId: string,
  answers: AnswerInput[],
): Promise<ApiResult<{ saved: number }>> {
  return api.patch(`/companies/${companyId}/questionnaires/owner/answers`, { answers });
}

export async function submitOwnerAnswers(
  companyId: string,
  answers: AnswerInput[],
): Promise<ApiResult<{ completed: true }>> {
  return api.post(`/companies/${companyId}/questionnaires/owner/submit`, { answers });
}

// =============================== PIC (public, magic-link) ===============================

export async function getPicQuestionnaire(
  token: string,
): Promise<ApiResult<PicQuestionnaireData>> {
  return api.get(`/q/${token}/questionnaire`);
}

export async function savePicAnswers(
  token: string,
  answers: AnswerInput[],
): Promise<ApiResult<{ saved: number }>> {
  return api.patch(`/q/${token}/answers`, { answers });
}

export async function submitPicAnswers(
  token: string,
  answers: AnswerInput[],
  acceptPolicyVersion: string,
): Promise<ApiResult<{ submitted: true }>> {
  // UU PDP: the explicitly-accepted privacy-policy version travels with the submit.
  return api.post(`/q/${token}/submit`, { answers, acceptPolicyVersion });
}

// =============================== hooks ===============================

/** Map an API error code to friendly Indonesian copy for link/load failures. */
export function friendlyError(code: string, fallback = 'Terjadi kesalahan. Coba lagi.'): string {
  switch (code) {
    case 'magic_link_expired':
      return 'Tautan ini sudah kedaluwarsa. Mohon minta tautan baru ke perusahaan Anda.';
    case 'magic_link_invalid':
      return 'Tautan tidak valid. Periksa kembali tautan yang Anda terima.';
    case 'already_submitted':
      return 'Kuesioner ini sudah dikirim. Terima kasih.';
    case 'not_found':
      return 'Kuesioner belum tersedia. Mohon tunggu tim Nine Co menyiapkannya.';
    case 'validation_error':
      return 'Masih ada pertanyaan wajib yang belum dijawab.';
    case 'rate_limited':
      return 'Terlalu banyak percobaan. Mohon tunggu sebentar.';
    default:
      return fallback;
  }
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave. Call `schedule(answers)` whenever the answer map changes;
 * after `delayMs` of quiet it PATCHes via `saver`. Coalesces rapid edits (only
 * the latest payload is sent) and surfaces a status for the UI indicator. The
 * pending timer is flushed/cancelled on unmount.
 */
export function useAutosave(
  saver: (answers: AnswerInput[]) => Promise<ApiResult<{ saved: number }>>,
  delayMs = 800,
): { status: SaveStatus; schedule: (answers: AnswerInput[]) => void; flush: () => Promise<void> } {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<AnswerInput[] | null>(null);
  const saverRef = useRef(saver);
  saverRef.current = saver;

  const run = useCallback(async () => {
    const payload = pending.current;
    pending.current = null;
    if (!payload || payload.length === 0) return;
    setStatus('saving');
    const res = await saverRef.current(payload);
    setStatus(res.ok ? 'saved' : 'error');
  }, []);

  const schedule = useCallback(
    (answers: AnswerInput[]) => {
      pending.current = answers;
      setStatus('saving');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), delayMs);
    },
    [delayMs, run],
  );

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    await run();
  }, [run]);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return { status, schedule, flush };
}

// ---- status → badge mapping (every QuestionnaireStatus + PIC status) ----

import type { BadgeTone } from '../components/ui/Badge';
import type { QuestionnaireStatus, PicContact, DeliveryStatus } from '@klinik/shared';

export const QUESTIONNAIRE_STATUS_META: Record<QuestionnaireStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: 'Antri AI', tone: 'draft' },
  in_review: { label: 'Direview', tone: 'progress' },
  approved: { label: 'Disetujui', tone: 'sent' },
  sent: { label: 'Terkirim', tone: 'sent' },
  collecting: { label: 'Mengumpulkan', tone: 'progress' },
  completed: { label: 'Selesai', tone: 'done' },
  failed: { label: 'Gagal', tone: 'critical' },
};

export function questionnaireStatusMeta(status: QuestionnaireStatus): { label: string; tone: BadgeTone } {
  return QUESTIONNAIRE_STATUS_META[status] ?? { label: status, tone: 'draft' };
}

export const PIC_STATUS_META: Record<PicContact['status'], { label: string; tone: BadgeTone }> = {
  pending: { label: 'Belum dibuka', tone: 'draft' },
  invited: { label: 'Terkirim', tone: 'sent' },
  in_progress: { label: 'Mengisi', tone: 'progress' },
  done: { label: 'Selesai', tone: 'done' },
};

export function picStatusMeta(status: PicContact['status']): { label: string; tone: BadgeTone } {
  return PIC_STATUS_META[status] ?? { label: status, tone: 'draft' };
}

export function deliveryStatusLabel(status: DeliveryStatus | null): string {
  if (status === 'sent') return 'Terkirim';
  if (status === 'failed') return 'Gagal kirim';
  if (status === 'queued') return 'Antri kirim';
  return 'Belum dikirim';
}

/** "2 jam lalu" style relative time for sentAt timestamps (Indonesian). */
export function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Baru saja';
  if (min < 60) return `${min} menit lalu`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} jam lalu`;
  const day = Math.floor(hr / 24);
  return `${day} hari lalu`;
}
